import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Paper, Grid, TextField, useTheme, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Skeleton, Chip, IconButton, Tooltip, Select, MenuItem } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavbar } from '../context/NavbarContext';
import { Add, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AddSiteDialog from '../components/AddSiteDialog';
import { logActivity, saveSiteToDynamoDB, getAllSites, deleteSiteFromDynamoDB, getUserFromDynamoDB, updateSiteStatus } from '../api/apiService';

// Styled Paper for DataGrid container (removed unused Item component)

const IndividualSitesPage = () => {
  const { isNavbarOpen } = useNavbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    client: '', // New field
    location: '',
    status: 'Ongoing',
    type: '',
    date: '',
    xCoordinate: '',
    yCoordinate: '',
  });
  const [error, setError] = useState('');
  const navigate = useNavigate(); // Hook for navigation
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  // Fetch data from DynamoDB and update rows
  useEffect(() => {
    const fetchSites = async () => {
      try {
        setLoading(true);
        const allSites = await getAllSites();
        
        if (userRole === 'Admin' || userRole === 'Manager') {
          // Admins and Managers can see all sites
          setRows(allSites);
        } else {
          // For Users and Engineers, fetch their assigned sites array
          const userData = await getUserFromDynamoDB(currentUser.email);
          const allowedSites = userData?.sites || (userData?.site?.id ? [userData.site] : []);
          // Filter sites to those in the user's assigned list
          const filteredSites = allSites.filter(site =>
            allowedSites.some(us => us.id === site.id)
          );
          setRows(filteredSites);
        }
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Error fetching sites:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchSites();
    }
  }, [currentUser, userRole]);

  // Responsive column visibility for mobile screens
  useEffect(() => {
    if (isMobile) {
      setColumnVisibilityModel({
        id: true,
        name: true,
        client: true,
        location: false,
        status: false,
        type: false,
        date: false,
        visit: true,
        actions: userRole === 'Admin'
      });
    } else if (isTablet) {
      setColumnVisibilityModel({
        id: true,
        name: true,
        client: true,
        location: true,
        status: true,
        type: false,
        date: false,
        visit: true,
        actions: userRole === 'Admin'
      });
    } else {
      setColumnVisibilityModel({}); // show all by default on desktop
    }
  }, [isMobile, isTablet, userRole]);

  // console.log(rows);

  // Handle FAB click to open the dialog
  const handleFabClick = () => {
    setIsDialogOpen(true);
  };

  // Refresh button removed per request

  // Handle dialog close
  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setFormData({
      id: '',
      name: '',
      client: '',
      location: '',
      status: 'Ongoing',
      type: '',
      date: '',
      xCoordinate: '',
      yCoordinate: '',
    });
    setError('');
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleFormSubmit = async (event) => {
    event.preventDefault();

    // Remove Project No. format validation
    // const projectNumberRegex = /^[A-Z]{3}\d{3,}$/; // Example: PRJ123, EXP456
    // if (!projectNumberRegex.test(formData.id)) {
    //   setError('Project No. must follow the format "ABC123" (e.g., PRJ1456, EXP156)');
    //   return;
    // }

    try {
      await saveSiteToDynamoDB({
        ...formData,
        status: formData.status === 'Active' ? 'Active' : 'Ongoing',
      });
      console.log('Site data uploaded to DynamoDB successfully!');
      console.log('Calling logActivity for user:', currentUser.email);
      try {
        await logActivity(currentUser.email, `Added site: ${formData.id}`);
      } catch (err) {
        console.error('Error logging activity to DynamoDB:', err);
      }
      
      // Refetch data after successful save
      const data = await getAllSites();
      setRows(data);
      
      handleDialogClose();
    } catch (error) {
      console.error('Error saving site to DynamoDB:', error);
    }
  };

  // Handle site deletion
  const handleDeleteSite = async (siteId) => {
    try {
      // Delete from DynamoDB
      await deleteSiteFromDynamoDB(siteId);
      // Remove from local state
      setRows((prevRows) => prevRows.filter((site) => site.id !== siteId));
      // Log delete activity
      try {
        await logActivity(currentUser.email, `Deleted site: ${siteId}`);
      } catch (err) {
        console.error('Error logging delete site activity to DynamoDB:', err);
      }
    } catch (error) {
      console.error('Error deleting site:', error);
    }
  };

  // Admin-only: update site status (Active | Ongoing)
  const handleStatusChange = async (siteId, newStatus) => {
    if (userRole !== 'Admin') return;
    setUpdatingStatusId(siteId);
    try {
      await updateSiteStatus(siteId, newStatus);
      setRows((prev) =>
        prev.map((site) => (site.id === siteId ? { ...site, status: newStatus } : site))
      );
      try {
        await logActivity(currentUser.email, `Updated site status: ${siteId} → ${newStatus}`);
      } catch (err) {
        console.error('Error logging status update:', err);
      }
    } catch (error) {
      console.error('Error updating site status:', error);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  // Filter rows by search (Project No., Name, or Client)
  const filteredRows = rows.filter((site) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      site.id.toLowerCase().includes(q) ||
      site.name.toLowerCase().includes(q) ||
      site.client.toLowerCase().includes(q)
    );
  });

  // DataGrid columns definition with responsive widths
  const baseColumns = [
    { field: 'id', headerName: 'Project No.', flex: 0.5 },
    { field: 'name', headerName: 'Project Name', flex: 1 },
    { field: 'client', headerName: 'Client', flex: 1 },
    { field: 'location', headerName: 'Location', flex: 1 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.75,
      sortable: false,
      renderCell: (params) => {
        const current = params.row.status === 'Active' ? 'Active' : 'Ongoing';
        if (userRole === 'Admin') {
          return (
            <Select
              size="small"
              value={current}
              disabled={updatingStatusId === params.row.id}
              onChange={(e) => handleStatusChange(params.row.id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              sx={{ minWidth: 110, fontSize: '0.85rem' }}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Ongoing">Ongoing</MenuItem>
            </Select>
          );
        }
        return params.row.status || '—';
      },
    },
    { field: 'type', headerName: 'Plant Type', flex: 0.5 },
    { field: 'date', headerName: 'Date of Installation', flex: 0.75 },
    {
      field: 'visit',
      headerName: 'Visit',
      flex: 0.5,
      renderCell: (params) => (
        <Button
          variant="contained"
          onClick={() => handleVisitClick(params.row.id)}
          size={isMobile ? "small" : "medium"}
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14),
            color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.30 : 0.20),
              boxShadow: 'none',
            },
            '&:focus-visible': {
              outline: (theme) => `2px solid ${theme.palette.brand?.royalBlue || theme.palette.primary.main}`,
              outlineOffset: '2px',
            },
          }}
        >
          Visit
        </Button>
      ),
    },
  ];

  // Include delete action column only for Admin role
  const columns = userRole === 'Admin'
    ? [
        ...baseColumns,
          {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.5,
            sortable: false,
            renderCell: (params) => (
              <Tooltip title="Delete Site">
                <IconButton
                  size={isMobile ? "small" : "medium"}
                  onClick={() => { setSiteToDelete(params.row.id); setDeleteModalOpen(true); }}
                  sx={{
                    color: (theme) => theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                    },
                    '&:focus-visible': {
                      outline: (theme) => `2px solid ${theme.palette.error.main}`,
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
            ),
          },
      ]
    : baseColumns;

  // Changes in the visit button to pass projectId as a prop
  const handleVisitClick = (projectId) => {
    navigate('/individual-project', { state: { projectId } });
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%', 
      background: theme.palette.background.default,
      margin: 0,
      padding: 0,
      overflow: 'auto',
    }}>
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          flexGrow: 1,
          padding: {
            xs: '12px',
            sm: '16px',
            md: '20px',
            lg: '24px',
            xl: '32px'
          },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
            md: isNavbarOpen ? '240px' : '70px',
            lg: isNavbarOpen ? '240px' : '70px',
            xl: isNavbarOpen ? '240px' : '70px'
          },
          marginTop: {
            xs: '65px',
            sm: '64px',
            md: '64px',
            lg: '64px',
            xl: '64px'
          },
          transition: 'margin-left 0.3s, padding 0.3s',
          position: 'relative',
          maxWidth: '100% !important',
          overflow: 'auto',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: {
            xs: '12px',
            sm: '16px',
            md: '16px',
            lg: '20px',
            xl: '24px'
          },
          margin: 0,
          padding: 0,
          width: '100%',
          minHeight: '100%',
          overflow: 'visible',
        }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography
                variant="h4"
                fontWeight="600"
                sx={{
                  color: theme.palette.text.primary,
                  letterSpacing: '-0.5px',
                  marginTop: 0,
                  fontSize: {
                    xs: '1.5rem',
                    sm: '1.75rem',
                    md: '2rem',
                    lg: '2.25rem',
                    xl: '2.5rem'
                  }
                }}
              >
                Sites
              </Typography>
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>Manage sites • Last updated {lastUpdated.toLocaleTimeString()}</Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Box sx={{ width: { xs: '100%', sm: '220px', md: '260px' } }}>
                <TextField
                  placeholder="Search Sites"
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: (theme) => theme.palette.background.paper,
                      '&:hover fieldset': {
                        borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>
              {/* Refresh removed */}
              {(userRole === 'Admin' || userRole === 'Manager') && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleFabClick}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                  sx={{
                    backgroundColor: (theme) => alpha(theme.palette.brand?.jade || theme.palette.success.main, theme.palette.mode === 'dark' ? 0.22 : 0.14),
                    color: (theme) => theme.palette.brand?.jade || theme.palette.success.main,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.brand?.jade || theme.palette.success.main, theme.palette.mode === 'dark' ? 0.30 : 0.20),
                      boxShadow: 'none',
                    },
                    '&:focus-visible': {
                      outline: (theme) => `2px solid ${theme.palette.brand?.jade || theme.palette.success.main}`,
                      outlineOffset: '2px',
                    },
                  }}
                >
                  Add Site
                </Button>
              )}
            </Stack>
          </Stack>

          {/* Sites List / Table */}
          <Box sx={{ 
            height: {
              xs: 'auto',
              sm: '450px',
              md: '500px',
              lg: '550px',
              xl: '600px'
            },
            overflow: 'hidden'
          }}>
            {loading ? (
              <Skeleton variant="rounded" sx={{ width: '100%', height: '100%', borderRadius: '12px' }} />
            ) : (
              isMobile ? (
                <Grid container spacing={1}>
                  {filteredRows.map((site) => (
                    <Grid item xs={12} key={site.id}>
                      <Paper sx={{ p: 2, borderRadius: '12px', border: (theme) => `1px solid ${theme.palette.divider}`, backgroundColor: (theme) => theme.palette.background.paper }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Box>
                            <Typography sx={{ fontWeight: 600, color: (theme) => theme.palette.text.primary }}>{site.name || 'Unnamed'}</Typography>
                            <Typography sx={{ fontSize: '12px', color: (theme) => theme.palette.text.secondary }}>{site.id}</Typography>
                          </Box>
                          <Chip 
                            size="small" 
                            label={site.status || 'Unknown'} 
                            sx={{ 
                              backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.10), 
                              border: (theme) => `1px solid ${alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.32 : 0.24)}`, 
                              color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.14),
                              },
                            }} 
                          />
                        </Stack>
                        <Typography sx={{ fontSize: '13px', color: (theme) => theme.palette.text.secondary, mb: 0.5 }}>Client: {site.client || '-'}</Typography>
                        <Typography sx={{ fontSize: '13px', color: (theme) => theme.palette.text.secondary, mb: 1 }}>Location: {site.location || '-'}</Typography>
                        <Stack direction="row" spacing={1}>
                          <Button 
                            variant="contained" 
                            size="small" 
                            onClick={() => handleVisitClick(site.id)}
                            sx={{
                              backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14),
                              color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                              boxShadow: 'none',
                              '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.30 : 0.20),
                                boxShadow: 'none',
                              },
                            }}
                          >
                            Visit
                          </Button>
                          {userRole === 'Admin' && (
                            <Tooltip title="Delete Site">
                              <IconButton
                                size="small"
                                onClick={() => { setSiteToDelete(site.id); setDeleteModalOpen(true); }}
                                sx={{
                                  color: (theme) => theme.palette.error.main,
                                  '&:hover': {
                                    backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                                  },
                                  '&:focus-visible': {
                                    outline: (theme) => `2px solid ${theme.palette.error.main}`,
                                    outlineOffset: '2px',
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box 
                  sx={{ 
                    height: '100%', 
                    width: '100%', 
                    overflowX: 'auto', 
                    borderRadius: '12px'
                  }}
                >
                  <DataGrid 
                    rows={filteredRows} 
                    columns={columns} 
                    pageSize={5}
                    rowsPerPageOptions={[5]}
                    checkboxSelection
                    density="standard"
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(m) => setColumnVisibilityModel(m)}
                    getRowId={(row) => row.id}
                    sx={{
                      backgroundColor: (theme) => theme.palette.background.paper,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: '12px',
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : theme.palette.grey[50],
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                      },
                      '& .MuiDataGrid-columnHeaderTitle': {
                        color: (theme) => theme.palette.text.secondary,
                        fontWeight: 600,
                      },
                      '& .MuiDataGrid-iconSeparator': {
                        color: (theme) => theme.palette.divider,
                        opacity: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        color: (theme) => theme.palette.text.primary,
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.10 : 0.06)
                      },
                      '& .MuiDataGrid-selectedRowCount, & .MuiTablePagination-displayedRows': {
                        color: (theme) => theme.palette.text.secondary,
                      },
                      '& .MuiDataGrid-footerContainer': {
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        backgroundColor: (theme) => theme.palette.background.paper,
                      },
                      '& .MuiCheckbox-root.Mui-checked': {
                        color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                      },
                      '& .MuiDataGrid-toolbarContainer': {
                        padding: '8px',
                      },
                      '& .MuiTablePagination-actions button': {
                        color: (theme) => theme.palette.text.secondary,
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08)
                        }
                      }
                    }}
                  />
                </Box>
              )
            )}
          </Box>
        </Box>

        {/* Dialog */}
        <AddSiteDialog 
          isOpen={isDialogOpen} 
          onClose={handleDialogClose} 
          formData={formData} 
          onChange={handleInputChange} 
          onSubmit={handleFormSubmit} 
          error={error} 
        />

        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>Are you sure you want to delete this site?</DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button onClick={() => { handleDeleteSite(siteToDelete); setDeleteModalOpen(false); }} color="error">Delete</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default IndividualSitesPage;
